import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export const PASSWORD_POLICY_MIN_LENGTH = 12;
export const PASSWORD_POLICY_MAX_LENGTH = 128;

const COMMON_PASSWORDS = new Set([
  '12345678',
  '123456789',
  '1234567890',
  'password',
  'password1',
  'password123',
  'password1234',
  'qwerty123',
  'admin123',
  'changeme',
  'welcome123',
  'letmein',
  'abc123',
  'iloveyou',
  'monkey',
  'dragon',
]);

const RESTRICTED_TERMS = ['leoch', 'powersuite'];

const normalize = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]/g, '');

const getPersonalTerms = (disallowedTerms: readonly string[]): string[] => {
  const values = new Set<string>();

  disallowedTerms.forEach((term) => {
    const normalized = normalize(term);
    if (normalized.length >= 3) {
      values.add(normalized);
    }

    const [localPart = ''] = term.split('@');
    const normalizedLocalPart = normalize(localPart);
    if (normalizedLocalPart.length >= 3) {
      values.add(normalizedLocalPart);
    }

    localPart
      .split(/[^a-zA-Z0-9]+/)
      .map((part) => normalize(part))
      .filter((part) => part.length >= 3)
      .forEach((part) => values.add(part));
  });

  return [...values];
};

export type PasswordPolicyValidatorOptions = Readonly<{
  disallowedTerms?: readonly string[];
}>;

export const passwordPolicyValidator = ({
  disallowedTerms = [],
}: PasswordPolicyValidatorOptions = {}): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const rawValue = control.value;
    const password = typeof rawValue === 'string' ? rawValue : '';

    if (!password) {
      return null;
    }

    const normalizedPassword = normalize(password);
    if (!normalizedPassword) {
      return null;
    }

    if (COMMON_PASSWORDS.has(normalizedPassword)) {
      return { passwordTooCommon: true };
    }

    if (
      RESTRICTED_TERMS.some(
        (term) => term.length >= 3 && normalizedPassword.includes(term),
      )
    ) {
      return { passwordContainsRestrictedTerm: true };
    }

    const personalTerms = getPersonalTerms(disallowedTerms);
    if (
      personalTerms.some(
        (term) => term.length >= 3 && normalizedPassword.includes(term),
      )
    ) {
      return { passwordContainsPersonalInfo: true };
    }

    return null;
  };
};
